import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  AccreditationFlowEnumType,
  AccreditationStatusEnumType,
  InvestmentEntityTypeEnumType,
} from '__generated__/graphql';
import classNames from 'classnames';
import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import AccreditationProcessInfo from 'components/AccreditationProcessInfo';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Select from 'primitives/Select';

import AccreditationDeadlineBanner from './AccreditationDeadlineBanner';
import AccreditationKnowledgeSection from './AccreditationKnowledgeSection';
import { ACCREDITATION_APPLICATION_ROUTE } from './AccreditationResumeCard';
import AccreditationResumeSection from './AccreditationResumeSection';
import { getEligibilityOptions } from './EligibilityCriteria';
import JointAccreditationDetailsDialogText from './JointAccreditationDetailsDialogText';
import { UPSERT_ACCREDITATION_APPLICATION_MUTATION } from './mutations';
import { openNavigateAifSignUp } from './navigate-aif';
import { isIndianResidentEntity } from './residency';

const INVESTMENT_ENTITIES_QUERY = gql(`
  query FetchInvestmentEntitiesForAccreditation {
    dashboard {
      id
      investmentEntities {
        id
        name
        status
        type
        createdAt
        updatedAt
        kyc {
          id
          address {
            country
          }
        }
        investorApplication {
          id
          accreditationStatus
          accreditationFlow
          accreditationProofDocument {
            url
            name
          }
          accreditationEligibilityCriteria
        }
      }
      lastUsedInvestmentEntity {
        id
      }
    }
  }
`);

const AccreditationPage: FC = () => {
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [formData, setFormData] = useState<any>({});
  const [investmentEntityId, setInvestmentEntityId] = useState('');
  const [showNavigateAifHandoff, setShowNavigateAifHandoff] = useState(false);

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITIES_QUERY);

  const [upsertAccreditationApplication, { loading: mutationLoading, error: mutationError }] =
    useMutation(UPSERT_ACCREDITATION_APPLICATION_MUTATION, {
      refetchQueries: ['FetchInvestmentEntitiesForAccreditation'],
    });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntities = data.dashboard.investmentEntities;

  const hasSubmittedFormPreviously = (investmentEntityId: string) => {
    const investorApplication = investmentEntities.find(
      investmentEntity => investmentEntity.id === investmentEntityId
    )?.investorApplication;

    if (!investorApplication) return false;

    return (
      investorApplication.accreditationStatus !==
      AccreditationStatusEnumType.PendingInvestorResponse
    );
  };

  const getSelectedInvestmentEntity = () => {
    return investmentEntities.find(investmentEntity => investmentEntity.id === investmentEntityId);
  };

  const isOnCvlOnlineFlow =
    getSelectedInvestmentEntity()?.investorApplication?.accreditationFlow ===
    AccreditationFlowEnumType.CvlOnline;

  // Pre-submit hint only: which track this entity is heading for. The routing
  // decision itself uses the accreditationFlow the backend returns on save.
  const isEligibleForCvlOnlineFlow = isIndianResidentEntity(getSelectedInvestmentEntity());

  // The investor is about to begin getting accredited, rather than telling us
  // about an existing certificate or opting out.
  const isStartingAccreditation =
    formData.currentlyAccredited === 'NO' && formData.qualifiesForAccreditation === 'QUALIFY';

  const submitButtonLabel =
    isStartingAccreditation && isEligibleForCvlOnlineFlow ? 'Start Application' : 'Submit';

  const routeAfterSubmit = (accreditationFlow?: AccreditationFlowEnumType | null) => {
    if (accreditationFlow === AccreditationFlowEnumType.CvlOnline) {
      navigate(ACCREDITATION_APPLICATION_ROUTE);
      return;
    }

    // Navigate AIF opens in a new tab, which a browser blocks unless it comes
    // from a direct click - so surface a button rather than opening it here.
    if (accreditationFlow === AccreditationFlowEnumType.NavigateAif) {
      setShowNavigateAifHandoff(true);
    }
  };

  const submitAccreditationDetails = async (
    submittedFormData: any,
    accreditationProofDocument: any
  ) => {
    const accreditationStatus =
      submittedFormData.currentlyAccredited === 'YES'
        ? AccreditationStatusEnumType.PendingAlindiaVerification
        : submittedFormData.qualifiesForAccreditation === 'QUALIFY'
        ? AccreditationStatusEnumType.InitiateProcess
        : AccreditationStatusEnumType.NotAccredited;

    try {
      const result = await upsertAccreditationApplication({
        variables: {
          investmentEntityId,
          accreditationStatus,
          accreditationProofDocument,
          accreditationEligibilityCriteria:
            submittedFormData.accreditationEligibilityCriteria || [],
        },
      });

      routeAfterSubmit(result.data?.upsertAccreditationApplication?.accreditationFlow);
    } catch (err) {}
  };

  const renderContent = () => {
    return (
      <>
        <SidebarLayoutNavigation
          title="Accreditation"
          subTitle="Become an Accredited Investor today"
        />
        <SidebarLayoutContent>
          <div className="mb-24">
            <AccreditationDeadlineBanner />

            <AccreditationResumeSection investmentEntities={investmentEntities} />

            <section aria-labelledby="accreditation-status-heading" className="mb-16">
              <div className="mb-4">
                <h2 id="accreditation-status-heading" className="mb-1 font-bold text-gray-900">
                  Help us understand your accreditation status
                </h2>
                <p className="text-sm text-gray-600">
                  Fill out the form below to help us assist you better. Depending on your answers,
                  we may need to ask you for additional information.
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
                <Select
                  label="Investment Entity"
                  options={investmentEntities.map(investmentEntity => ({
                    label: investmentEntity?.name,
                    value: investmentEntity?.id,
                    disabled: investmentEntity.status !== 'ACTIVE',
                    labelSecondaryText:
                      investmentEntity.status === 'ACTIVE' ? undefined : 'Inactive',
                  }))}
                  onChange={value => {
                    setShowNavigateAifHandoff(false);
                    setInvestmentEntityId(value);
                  }}
                  value={investmentEntityId}
                  className="w-full"
                />
                {investmentEntityId && hasSubmittedFormPreviously(investmentEntityId) ? (
                  getSelectedInvestmentEntity()?.investorApplication?.accreditationStatus ===
                  AccreditationStatusEnumType.Accredited ? (
                    <p className="col-span-full text-sm text-green-500 mt-2">
                      We have verified your accreditation details for this investment entity. No
                      further action is required.
                    </p>
                  ) : getSelectedInvestmentEntity()?.investorApplication?.accreditationStatus ===
                    AccreditationStatusEnumType.NotAccredited ? (
                    <p className="col-span-full text-sm text-gray-500 mt-2">
                      Thank you for your response. If there is any change in your status, you can
                      resubmit your details.
                    </p>
                  ) : isOnCvlOnlineFlow ? null : (
                    <p className="col-span-full text-sm text-green-500 mt-2">
                      We have received your accreditation details for this investment entity. We
                      will review them and get back to you soon. If you would like, you can resubmit
                      your details.
                    </p>
                  )
                ) : null}
                {showNavigateAifHandoff ? (
                  <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="mb-1 text-sm font-semibold text-blue-900">
                      Your details have been saved
                    </h4>
                    <p className="text-sm text-blue-800">
                      Finish your accreditation in our partner&apos;s portal. Use the authentication
                      code <span className="font-mono font-semibold">ALTRUST</span> — the details
                      have also been emailed to you.
                    </p>
                    <button
                      type="button"
                      className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={openNavigateAifSignUp}
                    >
                      Continue to Navigate AIF
                    </button>
                  </div>
                ) : null}
                {investmentEntityId ? (
                  !hasSubmittedFormPreviously(investmentEntityId) ? (
                    <div className="mt-6">
                      <FormPanel
                        loading={loading || mutationLoading}
                        error={error || mutationError}
                        buttonRef={submitButtonRef}
                        onSubmit={async data =>
                          submitAccreditationDetails(
                            data,
                            data.accreditationProofDocument
                              ? data.accreditationProofDocument
                              : undefined
                          )
                        }
                        submitButtonLabel="Submit"
                      >
                        <FormInput
                          fullWidth
                          type="radio-group"
                          fieldName="currentlyAccredited"
                          label="Do you currently have a valid Accreditation Certificate issued by a SEBI-approved KYC Registration Agency?"
                          helperText="SEBI-approved KYC Registration Agencies are CDSL Ventures Limited (CVL) and NSDL Database Management Limited (NDML)"
                          defaultValue={''}
                          options={[
                            { value: 'NO', label: 'No' },
                            { value: 'YES', label: 'Yes' },
                          ]}
                          validators={{
                            required: true,
                          }}
                          onChange={value => {
                            setFormData(value);
                          }}
                        />
                        <FormInput
                          fieldName="accreditationProofDocument"
                          label="Accreditation Proof Document *"
                          helperText="Please provide an Accredited Investor (AI) Certificate"
                          type="upload"
                          defaultValue={''}
                          validators={{
                            required: true,
                          }}
                          conditionsToShow={{
                            matches: [
                              {
                                field: 'currentlyAccredited',
                                condition: '===',
                                value: 'YES',
                              },
                            ],
                          }}
                        />
                        <FormInput
                          fullWidth
                          type="radio-group"
                          fieldName="qualifiesForAccreditation"
                          label="Do you qualify (individually or with a joint applicant) to obtain an accreditation certificate?"
                          defaultValue={''}
                          options={[
                            {
                              value: 'DO_NOT_QUALIFY',
                              label: 'No, I do not qualify',
                            },
                            {
                              value: 'QUALIFY',
                              label: 'Yes, I qualify',
                            },
                          ]}
                          validators={{
                            required: true,
                          }}
                          conditionsToShow={{
                            matches: [
                              {
                                field: 'currentlyAccredited',
                                condition: '===',
                                value: 'NO',
                              },
                            ],
                          }}
                          onChange={value => {
                            setFormData(value);
                          }}
                        />
                        <div
                          className={classNames(
                            'my-1 col-span-full',
                            formData.currentlyAccredited === 'NO' ? 'block' : 'hidden',
                            getSelectedInvestmentEntity()?.type ===
                              InvestmentEntityTypeEnumType.Individual
                              ? 'block'
                              : 'hidden'
                          )}
                        >
                          <JointAccreditationDetailsDialogText />
                        </div>
                        <FormInput
                          fullWidth
                          type="checkbox-group"
                          fieldName="accreditationEligibilityCriteria"
                          label="Which of the following criteria do you qualify for?"
                          helperText="You can select multiple options if eligible"
                          defaultValue={''}
                          compact={true}
                          options={getEligibilityOptions(
                            getSelectedInvestmentEntity()?.type as InvestmentEntityTypeEnumType
                          )}
                          validators={{
                            required: true,
                          }}
                          conditionsToShow={{
                            matches: [
                              {
                                field: 'currentlyAccredited',
                                condition: '===',
                                value: 'NO',
                              },
                              {
                                field: 'qualifiesForAccreditation',
                                condition: '===',
                                value: 'QUALIFY',
                              },
                            ],
                          }}
                        />
                        <div className="my-5" />
                        <ConfirmButton
                          onConfirm={() => {
                            // The handoff happens once the save succeeds - see
                            // routeAfterSubmit - so a failed save no longer sends
                            // the investor onward.
                            // @ts-ignore
                            submitButtonRef.current?.submit();
                          }}
                          confirmButtonLabel={
                            isStartingAccreditation
                              ? isEligibleForCvlOnlineFlow
                                ? 'Start Application'
                                : 'Initiate Process'
                              : 'Submit'
                          }
                          title="Submit your accreditation details?"
                          description="You can modify your submission anytime."
                          descriptionComponent={
                            isStartingAccreditation ? (
                              isEligibleForCvlOnlineFlow ? (
                                <p className="col-span-full mt-4 text-xs text-slate-600">
                                  You can complete your accreditation application right here on the
                                  platform. We&apos;ll take you to it once your details are saved.
                                </p>
                              ) : (
                                <AccreditationProcessInfo className="mt-4" />
                              )
                            ) : null
                          }
                          className={`col-span-full ${
                            submitButtonLabel === 'Submit' ? 'w-24' : 'w-48'
                          }`}
                        >
                          {submitButtonLabel}
                        </ConfirmButton>
                      </FormPanel>
                    </div>
                  ) : getSelectedInvestmentEntity()?.investorApplication?.accreditationStatus ===
                    AccreditationStatusEnumType.Accredited ? (
                    <></>
                  ) : (
                    <div className="mt-6">
                      <FormPanelWithReadMode
                        loading={loading || mutationLoading}
                        error={error || mutationError}
                        onSubmit={async formData =>
                          submitAccreditationDetails(
                            formData,
                            getSelectedInvestmentEntity()?.investorApplication
                              ?.accreditationProofDocument !== formData.accreditationProofDocument
                              ? formData.accreditationProofDocument
                              : undefined
                          )
                        }
                        submitButtonLabel="Submit"
                      >
                        <FormInput
                          fullWidth
                          type="radio-group"
                          fieldName="currentlyAccredited"
                          label="Do you currently have a valid Accreditation Certificate issued by a SEBI-approved KYC Registration Agency?"
                          helperText="SEBI-approved KYC Registration Agencies are CDSL Ventures Limited (CVL) and NSDL Database Management Limited (NDML)"
                          defaultValue={
                            getSelectedInvestmentEntity()?.investorApplication
                              ?.accreditationStatus ===
                            AccreditationStatusEnumType.PendingAlindiaVerification
                              ? 'YES'
                              : 'NO'
                          }
                          options={[
                            { value: 'NO', label: 'No' },
                            { value: 'YES', label: 'Yes' },
                          ]}
                          validators={{
                            required: true,
                          }}
                        />
                        <FormInput
                          fieldName="accreditationProofDocument"
                          label="Accreditation Proof Document *"
                          helperText="Please provide an Accredited Investor (AI) Certificate"
                          type="upload"
                          defaultValue={
                            getSelectedInvestmentEntity()?.investorApplication
                              ?.accreditationProofDocument
                          }
                          validators={{
                            required: true,
                          }}
                          conditionsToShow={{
                            matches: [
                              {
                                field: 'currentlyAccredited',
                                condition: '===',
                                value: 'YES',
                              },
                            ],
                          }}
                        />
                        <FormInput
                          fullWidth
                          type="radio-group"
                          fieldName="qualifiesForAccreditation"
                          label="Do you qualify to obtain an accreditation certificate?"
                          defaultValue={
                            getSelectedInvestmentEntity()?.investorApplication
                              ?.accreditationStatus === AccreditationStatusEnumType.NotAccredited
                              ? 'DO_NOT_QUALIFY'
                              : 'QUALIFY'
                          }
                          options={[
                            {
                              value: 'DO_NOT_QUALIFY',
                              label: 'No, I do not qualify',
                            },
                            {
                              value: 'QUALIFY',
                              label: 'Yes, I qualify',
                            },
                          ]}
                          validators={{
                            required: true,
                          }}
                          conditionsToShow={{
                            matches: [
                              {
                                field: 'currentlyAccredited',
                                condition: '===',
                                value: 'NO',
                              },
                            ],
                          }}
                          onChange={value => {
                            setFormData(value);
                          }}
                        />
                        <FormInput
                          fullWidth
                          type="checkbox-group"
                          fieldName="accreditationEligibilityCriteria"
                          label="Which of the following criteria do you qualify for?"
                          helperText="You can select multiple options if eligible"
                          defaultValue={
                            getSelectedInvestmentEntity()?.investorApplication
                              ?.accreditationEligibilityCriteria
                          }
                          compact={true}
                          options={getEligibilityOptions(
                            getSelectedInvestmentEntity()?.type as InvestmentEntityTypeEnumType
                          )}
                          validators={{
                            required: true,
                          }}
                          conditionsToShow={{
                            matches: [
                              {
                                field: 'currentlyAccredited',
                                condition: '===',
                                value: 'NO',
                              },
                              {
                                field: 'qualifiesForAccreditation',
                                condition: '===',
                                value: 'QUALIFY',
                              },
                            ],
                          }}
                        />
                      </FormPanelWithReadMode>
                    </div>
                  )
                ) : null}
              </div>
            </section>

            <hr className="mb-10 border-gray-200" />

            <AccreditationKnowledgeSection />
          </div>
        </SidebarLayoutContent>
      </>
    );
  };

  return <SidebarLayout>{renderContent()}</SidebarLayout>;
};

export default AccreditationPage;
